/**
 * Step Navigation Component - Phase 4 Implementation
 *
 * Navigation controls for moving between wizard steps.
 */

import React from 'react';
import { Box, Button, Alert, Typography, Divider } from '@mui/material';
import {
  ChevronLeft as PreviousIcon,
  ChevronRight as NextIcon,
  Check as SubmitIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { RegistrationFlowState } from '../hooks/useRegistrationFlow';

export interface StepNavigationProps {
  flowState: RegistrationFlowState;
  onNext: () => void;
  onPrevious: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  hasUnsavedChanges: boolean;
}

export const StepNavigation: React.FC<StepNavigationProps> = ({
  flowState,
  onNext,
  onPrevious,
  onCancel,
  isSubmitting,
  hasUnsavedChanges,
}) => {
  // Debug logging for step navigation state
  console.log(`🎯 [STEP NAVIGATION] Current state:`, {
    currentStep: flowState.currentStep,
    canAdvance: flowState.canAdvance,
    canGoBack: flowState.canGoBack,
    isSubmitting,
    stepName: flowState.stepName
  });
  const getNextButtonText = (): string => {
    if (flowState.isLastStep) {
      return 'Submit Asset';
    }
    return 'Next';
  };

  const getNextButtonIcon = () => {
    if (flowState.isLastStep) {
      return <SubmitIcon />;
    }
    return <NextIcon />;
  };

  const getDisabledReason = (): string | null => {
    if (isSubmitting) return null;
    if (!flowState.canAdvance) {
      switch (flowState.currentStep) {
        case 0:
          return 'Please select a layer to continue';
        case 1:
          return 'Please select category and subcategory to continue';
        case 2:
          return 'Please upload files to continue';
        case 3:
          return 'Please complete AI processing to continue';
        case 4:
          return 'Please review and validate metadata to continue';
        default:
          return 'Please complete current step to continue';
      }
    }
    return null;
  };

  const disabledReason = getDisabledReason();

  return (
    <Box>
      <Divider />

      {/* Validation message */}
      {disabledReason && (
        <Alert severity="info" sx={{ m: 2, mb: 1 }}>
          <Typography variant="body2">{disabledReason}</Typography>
        </Alert>
      )}

      {/* Unsaved changes warning */}
      {hasUnsavedChanges && (
        <Alert severity="warning" sx={{ m: 2, mb: 1 }}>
          <Typography variant="body2">
            You have unsaved changes. Make sure to complete the current step
            before navigating away.
          </Typography>
        </Alert>
      )}

      {/* Navigation buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'background.default',
        }}
      >
        {/* Left side - Back or Cancel button */}
        <Box>
          {!flowState.isFirstStep ? (
            <Button
              variant="outlined"
              onClick={onPrevious}
              startIcon={<PreviousIcon />}
              disabled={isSubmitting || !flowState.canGoBack}
            >
              Back
            </Button>
          ) : (
            <Button
              variant="text"
              onClick={onCancel}
              startIcon={<CancelIcon />}
              disabled={isSubmitting}
              color="inherit"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              Cancel
            </Button>
          )}
        </Box>

        {/* Right side - Next/Submit button */}
        <Box>
          <Button
            variant="contained"
            onClick={onNext}
            endIcon={getNextButtonIcon()}
            disabled={isSubmitting || !flowState.canAdvance}
            color={flowState.isLastStep ? 'success' : 'primary'}
            size="large"
            sx={{
              minWidth: 120,
              fontWeight: 'bold',
            }}
          >
            {isSubmitting ? 'Processing...' : getNextButtonText()}
          </Button>
        </Box>
      </Box>

      {/* Progress indicator text */}
      <Box sx={{ textAlign: 'center', py: 1, bgcolor: 'background.default' }}>
        <Typography variant="caption" color="text.secondary">
          Step {flowState.currentStep + 1} of {flowState.totalSteps} •{' '}
          {flowState.stepProgress}% Complete
        </Typography>
      </Box>
    </Box>
  );
};

export default StepNavigation;
